/**
 * Wistia Video Progress Tracker
 *
 * Tracks video progress for the Wistia Player Web Component (`<wistia-player>`)
 * and POSTs progress updates to the server. Also handles resume-from-last-position.
 *
 * @see {@link https://docs.wistia.com/docs/wistia-player-web-component} Wistia Player Web Component
 */

function initVideoProgressTracker() {
  const playerEl = document.querySelector('wistia-player[data-video-id]');

  if (!playerEl) {
    return;
  }

  const videoId = playerEl.getAttribute('data-video-id');

  if (!videoId) {
    return;
  }

  let lastSentAt = 0;
  let lastProgress = -1;

  const resumeProgress = Number(playerEl.dataset.resumeProgress || 0);
  const resumeCompleted = playerEl.dataset.resumeCompleted === '1';

  const sendProgress = async (progress, completed = false, force = false) => {
    const clamped = Math.max(0, Math.min(100, Number(progress) || 0));
    const now = Date.now();

    if (!force) {
      const withinThrottleWindow = now - lastSentAt < 5000;
      const smallProgressDelta = Math.abs(clamped - lastProgress) < 2;

      if (withinThrottleWindow && smallProgressDelta) {
        return;
      }
    }

    lastSentAt = now;
    lastProgress = clamped;

    const body = {
      videoId,
      progress: clamped,
      completed,
    };

    if (window.csrfTokenName && window.csrfToken) {
      body[window.csrfTokenName] = window.csrfToken;
    }

    try {
      const response = await fetch('/video-progress/track', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': window.csrfToken || '',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        console.warn(
          `Video progress track request failed (${response.status}).`,
        );
      }
    } catch (error) {
      console.debug('Video progress tracking failed:', error);
    }
  };

  const getPercent = () => {
    const elapsed = playerEl.currentTime;
    const duration = playerEl.duration;

    if (
      !Number.isFinite(elapsed) ||
      !Number.isFinite(duration) ||
      duration <= 0
    ) {
      return null;
    }

    return (elapsed / duration) * 100;
  };

  const onReady = () => {
    // Resume from last saved position
    if (
      !resumeCompleted &&
      Number.isFinite(resumeProgress) &&
      resumeProgress >= 2 &&
      resumeProgress < 95
    ) {
      const duration = playerEl.duration;

      if (Number.isFinite(duration) && duration > 0) {
        const targetSeconds = Math.max(
          0,
          Math.min(duration - 2, (duration * resumeProgress) / 100),
        );

        if (targetSeconds >= 3) {
          playerEl.currentTime = targetSeconds;
          lastProgress = Math.max(lastProgress, resumeProgress);
        }
      }
    }

    playerEl.addEventListener('timeupdate', () => {
      const percent = getPercent();
      if (percent !== null) {
        sendProgress(percent, percent >= 95, false);
      }
    });

    playerEl.addEventListener('pause', () => {
      const percent = getPercent();
      if (percent !== null) {
        sendProgress(percent, percent >= 95, true);
      }
    });

    playerEl.addEventListener('end', () => {
      sendProgress(100, true, true);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        const percent = getPercent();
        if (percent !== null) {
          sendProgress(percent, percent >= 95, true);
        }
      }
    });

    window.addEventListener('beforeunload', () => {
      const percent = getPercent();
      if (percent !== null) {
        sendProgress(percent, percent >= 95, true);
      }
    });
  };

  // Wait for the <wistia-player> custom element to be registered, then initialise
  customElements.whenDefined('wistia-player').then(() => {
    if (playerEl.duration > 0) {
      // Player already has metadata — initialise immediately
      onReady();
    } else {
      // Wait for the player to signal it's ready with a valid duration
      playerEl.addEventListener('ready', onReady, { once: true });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoProgressTracker);
} else {
  initVideoProgressTracker();
}
